import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronRight, ClipboardCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Quality } from "@/lib/api";

/**
 * SurveyPrepView — Joint-Commission survey-preparedness workspace.
 *
 * Layout:
 *   • Grouped by TJC chapter (NPSG, EC, MM, HR, PC, ...).
 *   • Each row: Standard | Item | Status | Owner | Due | Evidence notes.
 *   • Status is color-coded: Ready = green, In progress = amber,
 *     Needs attention = red, Not started = gray.
 *   • Add / delete / inline-edit any field. Auto-saves on blur.
 *   • Included in the monthly XLSX as a "TJC Survey Prep" tab so leaders
 *     can drop the workbook straight into the survey binder.
 */
const STATUS_LABELS = {
  not_started: "Not started",
  in_progress: "In progress",
  ready: "Ready",
  needs_attention: "Needs attention",
};
const STATUS_STYLES = {
  ready: "bg-[#0284c7] text-white",
  in_progress: "bg-[#8a6b1a] text-white",
  needs_attention: "bg-[#b94a48] text-white",
  not_started: "bg-[#e2e8f0] text-[#3f4643]",
};
const STATUS_ROW_TINT = {
  ready: "bg-[#f0f9ff]",
  in_progress: "bg-[#fff6e5]",
  needs_attention: "bg-[#fbebe7]",
  not_started: "",
};

export default function SurveyPrepView({ directors }) {
  const [items, setItems] = useState(null);
  const [openChapters, setOpenChapters] = useState({});
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await Quality.listSurveyPrep();
      setItems(list);
      // Expand every chapter by default the first time so nothing is hidden.
      setOpenChapters((prev) => {
        if (Object.keys(prev).length) return prev;
        const next = {};
        for (const it of list) next[it.chapter] = true;
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load survey prep");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const m = {};
    for (const it of items || []) {
      (m[it.chapter] = m[it.chapter] || []).push(it);
    }
    return m;
  }, [items]);

  const update = async (id, patch) => {
    try {
      await Quality.updateSurveyItem(id, patch);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update item");
    }
  };
  const remove = async (id) => {
    if (!confirm("Remove this survey-prep item? (History is preserved.)")) return;
    await Quality.deleteSurveyItem(id);
    await load();
  };

  if (items === null) {
    return <div className="p-8 text-center text-sm text-[#475569]">Loading survey prep…</div>;
  }

  // Summary counts across all statuses so leaders see readiness at a glance.
  const counts = { ready: 0, in_progress: 0, needs_attention: 0, not_started: 0 };
  for (const it of items) counts[it.status] = (counts[it.status] || 0) + 1;
  const total = items.length;

  return (
    <div className="px-6 py-4 space-y-4 overflow-auto" data-testid="quality-survey-prep">
      <div className="flex flex-wrap items-center gap-3">
        <ClipboardCheck className="w-5 h-5 text-[#0284c7]" />
        <div>
          <div className="font-heading text-sm font-semibold text-[#0f172a]">Joint Commission Survey Preparedness</div>
          <div className="text-xs text-[#475569]">Chapter-organized readiness tracker. Included on the monthly Excel export as its own tab.</div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap text-xs">
          <StatusPill count={counts.ready || 0} total={total} status="ready" />
          <StatusPill count={counts.in_progress || 0} total={total} status="in_progress" />
          <StatusPill count={counts.needs_attention || 0} total={total} status="needs_attention" />
          <StatusPill count={counts.not_started || 0} total={total} status="not_started" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddOpen((v) => !v)}
            data-testid="quality-survey-toggle-add"
            className="border-[#e2e8f0]"
          >
            <Plus className="w-4 h-4 mr-1" /> {addOpen ? "Close" : "Add item"}
          </Button>
        </div>
      </div>

      {addOpen && <AddItemForm directors={directors} onAdded={async () => { setAddOpen(false); await load(); }} />}

      {Object.entries(grouped).map(([chapter, list]) => {
        const isOpen = openChapters[chapter] !== false;
        return (
          <div key={chapter} className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden" data-testid={`survey-chapter-${chapter}`}>
            <button
              onClick={() => setOpenChapters((s) => ({ ...s, [chapter]: !isOpen }))}
              className="w-full flex items-center justify-between px-4 py-2 bg-[#faf9f5] hover:bg-[#f4f0e2] transition-colors border-b border-[#e2e8f0]"
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-heading text-sm font-semibold text-[#0f172a]">{chapter}</span>
                <span className="text-xs text-[#475569]">· {list.length} item{list.length > 1 ? "s" : ""}</span>
              </div>
            </button>
            {isOpen && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-semibold tracking-widest uppercase text-[#475569] border-b border-[#f0efe9]">
                    <th className="px-3 py-1.5" style={{ width: 140 }}>Standard</th>
                    <th className="px-3 py-1.5">Item</th>
                    <th className="px-3 py-1.5" style={{ width: 170 }}>Status</th>
                    <th className="px-3 py-1.5" style={{ width: 180 }}>Owner</th>
                    <th className="px-3 py-1.5" style={{ width: 140 }}>Due</th>
                    <th className="px-3 py-1.5" style={{ width: 280 }}>Evidence / Notes</th>
                    <th className="px-3 py-1.5" style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => (
                    <SurveyRow
                      key={item.id}
                      item={item}
                      directors={directors}
                      onUpdate={update}
                      onRemove={remove}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
      {items.length === 0 && (
        <div className="rounded-lg border border-dashed border-[#e0d9c9] bg-[#faf7ef] px-5 py-8 text-center text-sm text-[#475569]">
          No survey-prep items yet. Click <em>Add item</em> to start your survey-preparedness plan.
        </div>
      )}
    </div>
  );
}

function StatusPill({ count, total, status }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <span
      className={[
        "px-2 py-0.5 rounded font-semibold text-[10px] tracking-wider uppercase",
        STATUS_STYLES[status],
      ].join(" ")}
      data-testid={`survey-status-summary-${status}`}
      title={`${count}/${total}`}
    >
      {STATUS_LABELS[status]} · {count} ({pct}%)
    </span>
  );
}

function SurveyRow({ item, directors, onUpdate, onRemove }) {
  const [local, setLocal] = useState(item);
  useEffect(() => setLocal(item), [item]);

  const flush = (patch) => {
    // Only patch when the user actually changed a field; a value equal to the
    // remote copy means the user tabbed through without editing.
    const changed = Object.entries(patch).filter(([k, v]) => (item[k] || "") !== (v || ""));
    if (!changed.length) return;
    onUpdate(item.id, Object.fromEntries(changed));
  };

  return (
    <tr className={`border-b border-[#f0efe9] ${STATUS_ROW_TINT[local.status] || ""}`} data-testid={`survey-item-${item.id}`}>
      <td className="px-3 py-2 font-mono text-xs">
        <Input
          value={local.standard || ""}
          onChange={(e) => setLocal({ ...local, standard: e.target.value })}
          onBlur={() => flush({ standard: local.standard })}
          className="h-7 text-xs font-mono border-[#e2e8f0] bg-transparent"
          data-testid={`survey-standard-${item.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={local.title || ""}
          onChange={(e) => setLocal({ ...local, title: e.target.value })}
          onBlur={() => flush({ title: local.title })}
          className="h-7 text-sm border-[#e2e8f0] bg-transparent"
          data-testid={`survey-title-${item.id}`}
        />
        {item.description && (
          <div className="text-[11px] text-[#475569] italic mt-0.5 line-clamp-2">{item.description}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <select
          value={local.status}
          onChange={(e) => {
            setLocal({ ...local, status: e.target.value });
            onUpdate(item.id, { status: e.target.value });
          }}
          className={[
            "w-full h-7 text-xs font-semibold rounded px-2 border border-[#e2e8f0]",
            STATUS_STYLES[local.status],
          ].join(" ")}
          data-testid={`survey-status-${item.id}`}
        >
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v} className="text-[#0f172a] bg-white">{l}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <select
          value={local.owner_director_id || ""}
          onChange={(e) => {
            setLocal({ ...local, owner_director_id: e.target.value });
            onUpdate(item.id, { owner_director_id: e.target.value });
          }}
          className="w-full h-7 text-xs rounded px-2 border border-[#e2e8f0] bg-transparent"
          data-testid={`survey-owner-${item.id}`}
        >
          <option value="">— unassigned —</option>
          {directors.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <Input
          type="date"
          value={local.due_date || ""}
          onChange={(e) => setLocal({ ...local, due_date: e.target.value })}
          onBlur={() => flush({ due_date: local.due_date })}
          className="h-7 text-xs border-[#e2e8f0] bg-transparent"
          data-testid={`survey-due-${item.id}`}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={local.evidence_notes || ""}
          onChange={(e) => setLocal({ ...local, evidence_notes: e.target.value })}
          onBlur={() => flush({ evidence_notes: local.evidence_notes })}
          placeholder="e.g. Evidence in binder tab 3; audit passed 08/10"
          className="h-7 text-xs border-[#e2e8f0] bg-transparent"
          data-testid={`survey-evidence-${item.id}`}
        />
      </td>
      <td className="px-1 py-2 text-right">
        <button
          onClick={() => onRemove(item.id)}
          className="p-1 text-[#475569] hover:text-[#b94a48] hover:bg-[#fbebe7] rounded"
          title="Remove item"
          data-testid={`survey-delete-${item.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

function AddItemForm({ directors, onAdded }) {
  const [form, setForm] = useState({
    chapter: "",
    standard: "",
    title: "",
    description: "",
    status: "not_started",
    owner_director_id: "",
    due_date: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.chapter.trim() || !form.title.trim()) {
      toast.error("Chapter and item title are required");
      return;
    }
    setBusy(true);
    try {
      await Quality.createSurveyItem(form);
      onAdded();
      toast.success("Item added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add item");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#e2e8f0] bg-[#faf7ef] p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="survey-add-form">
      <div>
        <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Chapter</Label>
        <Input value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} placeholder="e.g. NPSG, Environment of Care" className="mt-1 border-[#e2e8f0]" data-testid="survey-new-chapter" />
      </div>
      <div>
        <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Standard</Label>
        <Input value={form.standard} onChange={(e) => setForm({ ...form, standard: e.target.value })} placeholder="e.g. NPSG.15.01.01" className="mt-1 border-[#e2e8f0] font-mono text-xs" data-testid="survey-new-standard" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Item title</Label>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Suicide-risk screening compliance" className="mt-1 border-[#e2e8f0]" data-testid="survey-new-title" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Description</Label>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Anything a surveyor should know at a glance" className="mt-1 border-[#e2e8f0]" data-testid="survey-new-description" />
      </div>
      <div>
        <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Owner</Label>
        <select value={form.owner_director_id} onChange={(e) => setForm({ ...form, owner_director_id: e.target.value })} className="mt-1 w-full h-9 border border-[#e2e8f0] rounded px-2 text-sm bg-white" data-testid="survey-new-owner">
          <option value="">— unassigned —</option>
          {directors.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
        </select>
      </div>
      <div>
        <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Due date</Label>
        <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 border-[#e2e8f0]" data-testid="survey-new-due" />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <Button disabled={busy} onClick={submit} className="bg-[#0284c7] hover:bg-[#0369a1] text-white" data-testid="survey-new-save">
          <Plus className="w-4 h-4 mr-1.5" /> Add item
        </Button>
      </div>
    </div>
  );
}
